import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FaSearch, FaFilter, FaLayerGroup, FaTimes, FaCompactDisc, FaMusic, FaPlay } from 'react-icons/fa';
import AlbumCard from '../components/AlbumCard';
import '../styles/HomePage.css';
import TagFilter from '../components/TagFilter';

function HomePage({ playTrack, addToQueue }) {
  const [albums, setAlbums] = useState([]);
  const [collections, setCollections] = useState([]);
  const [showCollections, setShowCollections] = useState(true);
  const [showAlbums, setShowAlbums] = useState(true);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [trackResults, setTrackResults] = useState([]);
  const [suggestions, setSuggestions] = useState([]);
  const [category, setCategory] = useState('');
  const [sortBy, setSortBy] = useState('title');
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [showFilters, setShowFilters] = useState(false);

  // Collection modal
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [collectionAlbums, setCollectionAlbums] = useState([]);
  const [recentAlbums, setRecentAlbums] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [tagAlbums, setTagAlbums] = useState(null);
  const [loadingCollection, setLoadingCollection] = useState(false);

  const categories = [
    { value: '', label: 'Toutes les catégories' },
    { value: 'film_soundtrack', label: 'BO de Films' },
    { value: 'disney_park', label: 'Albums Disney Park' },
    { value: 'public_album', label: 'Albums Grand Public' }
  ];

  useEffect(() => {
    fetchAlbums();
    fetchRecentAlbums();
    fetchCollections();
  }, [category, sortBy, pagination.page]);

  useEffect(() => {
    if (searchQuery.length >= 2) {
      fetchSuggestions();
    } else {
      setSuggestions([]);
    }
  }, [searchQuery]);

  const fetchAlbums = async () => {
    try {
      setLoading(true);
      const response = await axios.get('/api/albums', {
        params: {
          category,
          search: searchQuery,
          sort: sortBy,
          page: pagination.page,
          limit: 24,
          exclude_collections: 'true'
        }
      });

      setAlbums(response.data.albums);
      setPagination({
        page: response.data.pagination.page,
        totalPages: response.data.pagination.totalPages
      });
    } catch (error) {
      console.error('Erreur chargement albums:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCollections = async () => {
    try {
      const response = await axios.get('/api/collections', {
        params: { category: category || undefined }
      });
      setCollections(response.data);
    } catch (error) {
      console.error('Erreur chargement collections:', error);
    }
  };

  const fetchRecentAlbums = async () => {
    try {
      const response = await axios.get("/api/albums/recent");
      setRecentAlbums(response.data);
    } catch (error) {
      console.error("Erreur chargement nouveautés:", error);
    }
  };
  const handleTagSelect = async (tagId) => {
    setSelectedTag(tagId);
    if (!tagId) { setTagAlbums(null); return; }
    try {
      const res = await axios.get(`/api/tags/${tagId}/albums`);
      setTagAlbums(res.data);
    } catch (e) { setTagAlbums([]); }
  };
  const openCollection = async (collection) => {
    try {
      setSelectedCollection(collection);
      setLoadingCollection(true);
      const response = await axios.get(`/api/collections/${collection.id}`);
      setCollectionAlbums(response.data.albums || []);
    } catch (error) {
      console.error('Erreur chargement collection:', error);
    } finally {
      setLoadingCollection(false);
    }
  };

  const closeCollection = () => {
    setSelectedCollection(null);
    setCollectionAlbums([]);
  };

  const fetchSuggestions = async () => {
    try {
      const response = await axios.get('/api/search/suggestions', {
        params: { q: searchQuery }
      });
      setSuggestions(response.data);
    } catch (error) {
      console.error('Erreur suggestions:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setPagination({ ...pagination, page: 1 });
    fetchAlbums();
    fetchRecentAlbums();
    fetchCollections();
    setSuggestions([]);
    if (searchQuery.length >= 2) { try { const resp = await axios.get("/api/tracks/search", { params: { q: searchQuery, limit: 10 } }); setTrackResults(resp.data); } catch (e) { setTrackResults([]); } } else { setTrackResults([]); }
  };

  const handleSuggestionClick = (suggestion) => {
    setSearchQuery(suggestion.title);
    setSuggestions([]);
    setPagination({ ...pagination, page: 1 });
    fetchAlbums();
    fetchRecentAlbums();
  };

  const handlePageChange = (newPage) => {
    setPagination({ ...pagination, page: newPage });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const getCoverUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    return `https://disney-music.gpworld.fr${url.startsWith('/') ? url : '/' + url}`;
  };

  return (
    <div className="home-page">
      {/* Barre de recherche et filtres */}
      <div className="search-section">
        <form onSubmit={handleSearch} className="search-bar">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Rechercher un album ou une collection..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">Rechercher</button>
        </form>

        {suggestions.length > 0 && (
          <div className="suggestions">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="suggestion-item"
                onClick={() => handleSuggestionClick(suggestion)}
              >
                <strong>{suggestion.title}</strong>
                {suggestion.artist && <span> - {suggestion.artist}</span>}
              </div>
            ))}
          </div>
        )}

        <button 
          className="filter-toggle-btn"
          onClick={() => setShowFilters(!showFilters)}
        >
          <FaFilter /> Filtres
        </button>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="filters">
          <div className="filter-group">
            <label>Catégorie :</label>
            <select 
              value={category} 
              onChange={(e) => {
                setCategory(e.target.value);
                setPagination({ ...pagination, page: 1 });
              }}
            >
              {categories.map(cat => (
                <option key={cat.value} value={cat.value}>
                  {cat.label}
                </option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Trier par :</label>
            <select 
              value={sortBy} 
              onChange={(e) => {
                setSortBy(e.target.value);
                setPagination({ ...pagination, page: 1 });
              }}
            >
              <option value="title">Titre</option>
              <option value="artist">Artiste</option>
              <option value="created_at">Date d'ajout</option>
            </select>
          </div>
        </div>
      )}

      {/* Filtre par tags */}
      <TagFilter onTagSelect={handleTagSelect} selectedTag={selectedTag} />
      {tagAlbums && (
        <div className="tag-results-section">
          <h2 className="section-title">🏷️ Albums filtrés ({tagAlbums.length})</h2>
          {tagAlbums.length === 0 ? <p className="tag-no-results">Aucun album avec ce tag</p> : (
            <div className="albums-grid">
              {tagAlbums.map(album => (
                <AlbumCard key={`tag-${album.id}`} album={album} playTrack={playTrack} addToQueue={addToQueue} />
              ))}
            </div>
          )}
        </div>
      )}
      {/* Résultats pistes */}
      {trackResults.length > 0 && (
        <div className="track-results-section">
          <h2 className="section-title"><FaMusic /> Pistes trouvées ({trackResults.length})</h2>
          <div className="track-results-list">
            {trackResults.map(track => (
              <div key={track.id} className="track-result-item" onClick={() => playTrack(track, trackResults)}>
                <div className="track-result-play"><FaPlay /></div>
                <div className="track-result-info">
                  <span className="track-result-title">{track.title}</span>
                  <span className="track-result-artist">{track.artist || track.album_artist} — {track.album_title}</span>
                </div>
                <span className="track-result-duration">{track.duration ? Math.floor(track.duration/60)+":"+String(Math.floor(track.duration%60)).padStart(2,"0") : ""}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Collections */}
      {collections.length > 0 && !searchQuery && (
        <div className="collections-section">
          <h2 className="section-title" onClick={() => setShowCollections(!showCollections)} style={{cursor:"pointer"}}><span style={{display:"inline-block",transition:"transform 0.3s",transform:showCollections?"rotate(90deg)":"rotate(0)"}}>▶</span> <FaLayerGroup /> Collections ({collections.length})</h2>
          {showCollections && <div className="collections-grid">
            {collections.map(collection => (
              <div 
                key={`col-${collection.id}`} 
                className="collection-card"
                onClick={() => openCollection(collection)}
              >
                <div className="collection-cover-stack">
                  {/* Effet de cartes empilées */}
                  <div className="stack-card stack-3"></div>
                  <div className="stack-card stack-2"></div>
                  <div className="stack-card stack-1">
                    {(collection.cover_url || collection.first_album_cover) ? (
                      <img 
                        src={getCoverUrl(collection.cover_url || collection.first_album_cover)} 
                        alt={collection.name} 
                      />
                    ) : (
                      <div className="collection-cover-placeholder">
                        <FaLayerGroup />
                      </div>
                    )}
                  </div>
                </div>
                <div className="collection-info">
                  <h3 className="collection-name">{collection.name}</h3>
                  <p className="collection-count">
                    <FaCompactDisc /> {collection.album_count} album{collection.album_count > 1 ? 's' : ''}
                  </p>
                </div>
              </div>
            ))}
          </div>}
        </div>
      )}

      {/* Nouveautés */}
      {recentAlbums.length > 0 && !searchQuery && (
        <div className="recent-section">
          <h2 className="section-title">🆕 Ajoutés récemment</h2>
          <div className="recent-scroll">
            {recentAlbums.map(album => (
              <AlbumCard key={`recent-${album.id}`} album={album} playTrack={playTrack} addToQueue={addToQueue} />
            ))}
          </div>
        </div>
      )}
      {/* Liste des albums (hors collections) */}
      {loading ? (
        <div className="loading">
          <div className="spinner"></div>
          <p>Chargement des albums...</p>
        </div>
      ) : (
        <>
          {albums.length === 0 && collections.length === 0 ? (
            <div className="no-results">
              <p>Aucun album trouvé.</p>
            </div>
          ) : albums.length > 0 && (
            <>
              {collections.length > 0 && !searchQuery && (
                <h2 className="section-title" onClick={() => setShowAlbums(!showAlbums)} style={{ marginTop: '20px', cursor: 'pointer' }}>
                  <span style={{display:"inline-block",transition:"transform 0.3s",transform:showAlbums?"rotate(90deg)":"rotate(0)"}}>▶</span> <FaCompactDisc /> Albums
                </h2>
              )}
              {showAlbums && <div className="albums-grid">
                {albums.map(album => (
                  <AlbumCard 
                    key={album.id} 
                    album={album} 
                    playTrack={playTrack} addToQueue={addToQueue} 
                  />
                ))}
              </div>}
            </>
          )}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="page-btn"
              >
                ← Précédent
              </button>

              <span className="page-info">
                Page {pagination.page} sur {pagination.totalPages}
              </span>

              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.totalPages}
                className="page-btn"
              >
                Suivant →
              </button>
            </div>
          )}
        </>
      )}

      {/* Modal Collection */}
      {selectedCollection && (
        <div className="collection-modal-overlay" onClick={closeCollection}>
          <div className="collection-modal" onClick={(e) => e.stopPropagation()}>
            <button className="collection-modal-close" onClick={closeCollection}>
              <FaTimes />
            </button>

            <div className="collection-modal-header">
              <div className="collection-modal-cover">
                {(selectedCollection.cover_url || selectedCollection.first_album_cover) ? (
                  <img 
                    src={getCoverUrl(selectedCollection.cover_url || selectedCollection.first_album_cover)} 
                    alt={selectedCollection.name} 
                  />
                ) : (
                  <div className="collection-cover-placeholder large">
                    <FaLayerGroup />
                  </div>
                )}
              </div>
              <div className="collection-modal-info">
                <h2>{selectedCollection.name}</h2>
                {selectedCollection.description && <p className="collection-desc">{selectedCollection.description}</p>}
                <p className="collection-modal-count">
                  {selectedCollection.album_count} album{selectedCollection.album_count > 1 ? 's' : ''}
                </p>
              </div>
            </div>

            <div className="collection-modal-albums">
              {loadingCollection ? (
                <div className="loading">
                  <div className="spinner"></div>
                  <p>Chargement...</p>
                </div>
              ) : collectionAlbums.length === 0 ? (
                <p className="no-albums">Aucun album dans cette collection</p>
              ) : (
                <div className="albums-grid">
                  {collectionAlbums.map(album => (
                    <AlbumCard
                      key={album.id}
                      album={album}
                      playTrack={playTrack} addToQueue={addToQueue}
                    />
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default HomePage;
